import { 
  Button, 
  Card, 
  CardHeader, 
  Drawer, 
  DrawerBody, 
  DrawerHeader, 
  DrawerHeaderTitle, 
  Title3,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Text,
  Subtitle2,
  Spinner,
  Dropdown,
  Option,
  TabList,
  Tab,
  Tooltip,
  Field,
  Textarea,
  Badge,
  Input
} from "@fluentui/react-components";
import {CheckmarkRegular, DeleteRegular, DismissRegular, PersonAddRegular, EyeRegular, PersonSubtractRegular, SettingsRegular, AddRegular, CopyRegular, SearchRegular } from "@fluentui/react-icons";
import { User } from "../../models/users";
import { useEffect, useState } from "react";
import { RoleDTO } from "../../models/role";
import UserGroupsDialog from "../dialogs/UserGroupsDialog";
import CopyUserGroupsDialog from "../dialogs/CopyUserGroupsDialog";
import UserGroupMembersDrawer from "./UserGroupMembersDrawer";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import NotificationDialog from "../dialogs/NotificationDialog";
import { deleteUserGroup, getUserGroups, getUserGroupsByModuleAndOrganizationID, getUserGroupsByUser, updateUserGroup } from "../../services/userGroupService";
import { getTasks, updateTask, createTenderingRequestTask } from "../../services/taskService";
import { getUsersByModuleAndRole } from "../../services/usersService";
import { CreateTask, TaskData, TaskListItem, TENDERING_ACCESS_REQUEST_TASK_TYPE_ID } from "../../models/task";
import { Module } from "../../models/module";
import { useAppContext } from "../../AppContext";
import { UpdateUserGroup, UserGroupEmail } from "../../models/userGroup";
import { Organization } from "../../models/organizations";
import { getOrganizations } from "../../services/organizationService";
import { convertToLocaleDateString } from "../../utils/date";
import TenderingRequestDialog from "../dialogs/TenderingRequestDialog";

type Props = {
  onClose: () => void;
  open: boolean;
  tab?: string;
};

const UserGroupsDrawer = (props: Props) => {
  const [users, setUsers] = useState<User[]>([]);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [filteredUserGroups, setFilteredUserGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState<boolean>(false);
  const [isCopyGroupsDialogOpen, setIsCopyGroupsDialogOpen] = useState<boolean>(false);
  const [groupSearch, setGroupSearch] = useState<string>("");
  const [isGroupMembersDrawerOpen, setIsGroupMembersDrawerOpen] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [actualTab, setActualTab] = useState<string>(props.tab ?? "groups");
  const [tenderings, setTenderings] = useState<Module[]>([]);
  const [selectedRamavtal, setSelectedRamavtal] = useState<string>("");
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [confirmationAction, setConfirmationAction] = useState<'accept' | 'deny' | 'delete' | 'leaveDirect' | 'leaveRequest' | null>(null);
  const [selectedTaskID, setSelectedTaskID] = useState<number | null>(null);
  const [selectedGroupToDelete, setSelectedGroupToDelete] = useState<any | null>(null);
  const [denyComment, setDenyComment] = useState<string>("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [ownPendingLeaveGroupIDs, setOwnPendingLeaveGroupIDs] = useState<Set<number>>(new Set());
  const [leaveTargetGroup, setLeaveTargetGroup] = useState<any | null>(null);
  const [leaveNotificationOpen, setLeaveNotificationOpen] = useState<boolean>(false);
  const [leaveNotificationText, setLeaveNotificationText] = useState<string>("");
  const [isRequestMoreGroupsDialogOpen, setIsRequestMoreGroupsDialogOpen] = useState<boolean>(false);
  const [moreGroupsTenderingID, setMoreGroupsTenderingID] = useState<string>("");
  const [isMoreGroupsGroupDialogOpen, setIsMoreGroupsGroupDialogOpen] = useState<boolean>(false);
  const [moreGroupsAvailableUserGroups, setMoreGroupsAvailableUserGroups] = useState<{ userGroupID: number; userGroupName: string }[]>([]);
  const [moreGroupsSelectedUserGroupIDs, setMoreGroupsSelectedUserGroupIDs] = useState<string[]>([]);
  const [moreGroupsAdminsMap, setMoreGroupsAdminsMap] = useState<Record<string, { userID: number; email: string; firstName: string; lastName: string }[]>>({});
  const [loadingMoreGroupsAdminIDs, setLoadingMoreGroupsAdminIDs] = useState<string[]>([]);
  const [ownPendingJoinGroupIDs, setOwnPendingJoinGroupIDs] = useState<Set<number>>(new Set());

  const { user, featurePermissions, userTasks, setUserTasks } = useAppContext();

  useEffect(() => {
    // Fetch users when the drawer is opened
    if (props.open) {
      setActualTab(props.tab ?? "groups");
      setTenderings(user.modules.filter((m: Module) => m.moduleType === 'Tendering'));
      const fetchUserGroups = async () => {
        try{
          setLoading(true);
          let userGroupResult: any[] = [];
          if(user && user.roles.some((r: RoleDTO) => r.roleType == 'General')){
            userGroupResult = await getUserGroups();
          }
          else{
            userGroupResult = await getUserGroupsByUser(user.user.userID);
            if(user && user.roles.some((r: RoleDTO) => r.roleName == "Ramavtalsansvarig" || r.roleName == "Kontoadmin")){
              const tenderingIDs = user.assignments.filter((a: any) => a.role.roleName == "Ramavtalsansvarig" || a.role.roleName == "Kontoadmin").map((a: any) => a.module.moduleID);
              const moduleGroupResults = await Promise.all(
                tenderingIDs.map((id: number) => getUserGroupsByModuleAndOrganizationID(id, user.organization.organizationID))
              );
              const merged = [...userGroupResult, ...moduleGroupResults.flat()];
              userGroupResult = merged.filter((g, i, arr) => arr.findIndex(x => x.userGroupID === g.userGroupID) === i);
            }
          }
          setUserGroups(userGroupResult);
          setFilteredUserGroups(userGroupResult);
        }
        catch (error) {
          console.error('Error fetching user groups:', error);
        }
        finally {
          setLoading(false);
        }
      };

      const fetchOrganizations = async () => {
        const orgResult = await getOrganizations();
        setOrganizations(orgResult.filter((o: Organization) => o.organizationTypeID == 2));
      };

      // Own outstanding requests, so "Lämna grupp" can be disabled and already-requested groups hidden from the join dialog
      const fetchOwnPendingAccessRequests = async () => {
        try {
          const response = await getTasks(true, 1000, 0, TENDERING_ACCESS_REQUEST_TASK_TYPE_ID);
          const ownPending: TaskListItem[] = (response?.tasks ?? []).filter((t: TaskListItem) => {
            const isPending = t.isTaskApproved === null || t.isTaskApproved === undefined;
            return t.taskIssuedByUserID === user.user.userID && isPending;
          });
          const leaveIDs = new Set<number>();
          const joinIDs = new Set<number>();
          ownPending.forEach((t: TaskListItem) => {
            const data = getTaskData(t.taskData);
            const groupID = data.TaskUserGroupID ?? data.taskUserGroupID;
            if (!groupID) return;
            if (getRequestType(t) === 'Leave') leaveIDs.add(groupID);
            else joinIDs.add(groupID);
          });
          setOwnPendingLeaveGroupIDs(leaveIDs);
          setOwnPendingJoinGroupIDs(joinIDs);
        }
        catch (error) {
          console.error('Error fetching own pending access requests:', error);
        }
      };

      if(user.roles.some((r: RoleDTO) => r.roleType == 'General')){
        fetchOrganizations();
      }
      fetchUserGroups();
      fetchOwnPendingAccessRequests();
    }
  }, [props.open]);

  const handleAddUsersClick = (userGroup: any) => {
    setSelectedGroup(userGroup);
    setIsGroupMembersDrawerOpen(true);
  };

  const handleRamavtalSelect = (tenderingId: string) => {
    setSelectedRamavtal(tenderingId);
    console.log('Selected ramavtal:', tenderingId);
  };

  const handleOrganizationSelect = (organizationId: string) => {
    setSelectedOrganization(organizationId);
    console.log('Selected organization:', organizationId);
  };

  // Filter userGroups based on selected organization and ramavtal
  useEffect(() => {
    let filtered = [...userGroups];

    if (selectedOrganization) {
      filtered = filtered.filter(ug => ug.organizationID?.toString() === selectedOrganization);
    }

    if (selectedRamavtal) {
      filtered = filtered.filter(ug => ug.moduleID?.toString() === selectedRamavtal);
    }

    // Free-text search across the group name, its ramavtal and its members
    const term = groupSearch.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(ug => {
        const ramavtalName = (ug.module?.moduleName ?? tenderings.find(t => t.moduleID === ug.moduleID)?.moduleName ?? "").toLowerCase();
        return (ug.userGroupName ?? "").toLowerCase().includes(term)
          || ramavtalName.includes(term)
          || (ug.userGroupAssignments ?? []).some((a: any) =>
            `${a.user?.email ?? ""} ${a.user?.firstName ?? ""} ${a.user?.lastName ?? ""}`.toLowerCase().includes(term));
      });
    }

    setFilteredUserGroups(filtered);
  }, [selectedOrganization, selectedRamavtal, userGroups, groupSearch, tenderings]);

  const handleUsersAdded = async () => {
    // Refetch user groups after adding users
    try {
      setLoading(true);
      let userGroupResult: any[] = [];
      if(user && user.roles.some((r: RoleDTO) => r.roleType == 'General')){
        userGroupResult = await getUserGroups();
      }
      else{
        userGroupResult = await getUserGroupsByUser(user.user.userID);
        if(user && user.roles.some((r: RoleDTO) => r.roleName == "Ramavtalsansvarig" || r.roleName == "Kontoadmin")){
          const tenderingIDs = user.assignments.filter((a: any) => a.role.roleName == "Ramavtalsansvarig" || a.role.roleName == "Kontoadmin").map((a: any) => a.module.moduleID);
          const moduleGroupResults = await Promise.all(
            tenderingIDs.map((id: number) => getUserGroupsByModuleAndOrganizationID(id, user.organization.organizationID))
          );
          const merged = [...userGroupResult, ...moduleGroupResults.flat()];
          userGroupResult = merged.filter((g, i, arr) => arr.findIndex(x => x.userGroupID === g.userGroupID) === i);
        }
      }
      setUserGroups(userGroupResult);
      // Keep selectedGroup in sync so the members drawer reflects the latest data
      setSelectedGroup((prev: any) =>
        prev ? userGroupResult.find((g: any) => g.userGroupID === prev.userGroupID) ?? prev : null
      );
    } catch (error) {
      console.error('Error fetching user groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const getModuleNameById = (moduleID: number): string => {
    const module = tenderings.find(m => m.moduleID === moduleID);
    return module ? module.moduleName : 'Okänt ramavtal';
  }

  const getTaskData = (taskData: TaskData | string): TaskData  => {
    const data: TaskData = JSON.parse(taskData as string) as TaskData;

    return data;
  }

  // Missing RequestType means the task predates this field and was always a join request; backend sends the enum as a number (0 = Join, 1 = Leave)
  const getRequestType = (task: TaskListItem): 'Join' | 'Leave' => {
    const data = getTaskData(task.taskData);
    const raw = data.RequestType ?? data.requestType;
    return raw === 1 || raw === 'Leave' ? 'Leave' : 'Join';
  }

  const getUserGroupName = (groupID: number): string => {
    const group = userGroups.find(g => g.userGroupID === groupID);
    return group ? group.userGroupName : 'Okänd grupp';
  }

  const onTaskDenied = (taskID: number) => {
    setSelectedTaskID(taskID);
    setConfirmationAction('deny');
    setDenyComment("");
    setIsConfirmationOpen(true);
  }

  const handleDenyConfirmed = async () => {
    if (selectedTaskID === null) return;
    
    let task = userTasks.find(t => t.taskID === selectedTaskID);
    
    if (task) {
      task.isTaskApproved = false;
      task.taskComment = denyComment.trim() || undefined;
      console.log(task);
      
      try{
        await updateTask(task?.taskID, task as TaskListItem);
        setUserTasks(userTasks.filter(t => t.taskID !== selectedTaskID));
      }
      catch (error) {
        console.error('Error updating task:', error);
      }
    }
    setIsConfirmationOpen(false);
    setSelectedTaskID(null);
    setConfirmationAction(null);
    setDenyComment("");
  }

  const onTaskAccepted = (taskID: number) => {
    setSelectedTaskID(taskID);
    setConfirmationAction('accept');
    setIsConfirmationOpen(true);
  }

  const handleAcceptConfirmed = async () => {
    if (selectedTaskID === null) return;
    
    let task = userTasks.find(t => t.taskID === selectedTaskID);
    
    if (task) {
      task.isTaskApproved = true;
      const taskData = getTaskData(task.taskData);
      const requestType = getRequestType(task);
      const userGroup = userGroups.find(ug => ug.userGroupID === taskData.TaskUserGroupID);
      let emails: UserGroupEmail[] = userGroup.userGroupAssignments.map((uga: any): UserGroupEmail => ({
        Email: uga.user.email,
        IsAdmin: uga.roles?.some((r: any) => r.roleName === "Behörighetsansvarig") ?? false
      }));

      const submitData: UpdateUserGroup = requestType === 'Leave'
        ? {
          UserGroupID: userGroup.userGroupID,
          Emails: emails.filter(e => e.Email !== taskData.UserEmail)
        }
        : {
          UserGroupID: userGroup.userGroupID,
          Emails: [...emails, { Email: taskData.UserEmail, IsAdmin: false }]
        };

      console.log(requestType === 'Leave' ? 'Removing user from group:' : 'Adding user to group:', submitData);
      try{
        await updateUserGroup(submitData);
        await updateTask(task?.taskID, task as TaskListItem);
        setUserTasks(userTasks.filter(t => t.taskID !== selectedTaskID));
      }
      catch (error) {
        console.error('Error updating task:', error);
      }
    }
    setIsConfirmationOpen(false);
    setSelectedTaskID(null);
    setConfirmationAction(null);
  }

  const isUserGroupAdmin = (userGroup: any): boolean => {
    if(user){
      const isGroupAdmin = userGroup.userGroupAssignments?.some((member: any) =>
        member.user?.userID === user.user.userID && member.roles?.some((r: any) => r.roleName === "Behörighetsansvarig")
      );
      const userAdministratedTenderings = user.assignments.filter((a: any) => a.role.roleName == "Ramavtalsansvarig" || a.role.roleName == "Kontoadmin").map((a: any) => a.module);
      
      return isGroupAdmin 
      || userAdministratedTenderings.some((t: any) => t.moduleID === userGroup.moduleID) 
      || user.roles.some((r: RoleDTO) => r.roleType == 'General');
    }
    else return false;
  }

  // Only sysadmin (General) or a Ramavtalsansvarig scoped to this group's own module — never a plain group admin
  const canDeleteUserGroup = (userGroup: any): boolean => {
    if (!user || !user.featurePermissions.some((fp: any) => fp.action === "Delete user group")) return false;

    const isGeneral = user.roles.some((r: RoleDTO) => r.roleType == 'General');
    const managesModule = user.assignments.some((a: any) => (a.role.roleName == "Ramavtalsansvarig" || a.role.roleName == "Kontoadmin") && a.module.moduleID === userGroup.moduleID);

    return isGeneral || managesModule;
  }

  const isUserGroupEmpty = (userGroup: any): boolean => {
    return !userGroup.userGroupAssignments || userGroup.userGroupAssignments.length === 0;
  }

  const onDeleteGroupClick = (userGroup: any) => {
    setSelectedGroupToDelete(userGroup);
    setConfirmationAction('delete');
    setIsConfirmationOpen(true);
  }

  const handleDeleteConfirmed = async () => {
    if (!selectedGroupToDelete) return;

    // Re-check emptiness client-side even though the backend also enforces this (and enforces the active-placement check)
    if (isUserGroupEmpty(selectedGroupToDelete)) {
      try {
        await deleteUserGroup(selectedGroupToDelete.userGroupID);
        setUserGroups(userGroups.filter(g => g.userGroupID !== selectedGroupToDelete.userGroupID));
        setFilteredUserGroups(filteredUserGroups.filter(g => g.userGroupID !== selectedGroupToDelete.userGroupID));
      } catch (error: any) {
        console.error('Error deleting user group:', error);
        const message = error?.response?.data?.message ?? "Ett fel uppstod när gruppen skulle raderas. Kontakta administratörerna för att lösa problemet.";
        setLeaveNotificationText(message);
        setLeaveNotificationOpen(true);
      }
    }

    setIsConfirmationOpen(false);
    setSelectedGroupToDelete(null);
    setConfirmationAction(null);
  }

  const isGroupMember = (userGroup: any): boolean => {
    return userGroup.userGroupAssignments?.some((member: any) => member.user?.userID === user.user.userID) ?? false;
  }

  const handleLeaveGroupClick = (userGroup: any) => {
    setLeaveTargetGroup(userGroup);
    // Someone who can already approve/deny leave requests for this group doesn't need to create one
    setConfirmationAction(isUserGroupAdmin(userGroup) ? 'leaveDirect' : 'leaveRequest');
    setIsConfirmationOpen(true);
  }

  const confirmLeaveGroupDirect = async () => {
    if (!leaveTargetGroup) return;

    const emails: UserGroupEmail[] = (leaveTargetGroup.userGroupAssignments ?? [])
      .filter((uga: any) => uga.user?.userID !== user.user.userID)
      .map((uga: any): UserGroupEmail => ({
        Email: uga.user.email,
        IsAdmin: uga.roles?.some((r: any) => r.roleName === "Behörighetsansvarig") ?? false
      }));

    const submitData: UpdateUserGroup = {
      UserGroupID: leaveTargetGroup.userGroupID,
      Emails: emails
    };

    try {
      await updateUserGroup(submitData);
      const removeSelf = (groups: any[]) => groups.map(g => g.userGroupID === leaveTargetGroup.userGroupID
        ? { ...g, userGroupAssignments: (g.userGroupAssignments ?? []).filter((uga: any) => uga.user?.userID !== user.user.userID) }
        : g);
      setUserGroups(prev => removeSelf(prev));
      setFilteredUserGroups(prev => removeSelf(prev));
      setLeaveNotificationText(`Du har lämnat gruppen ${leaveTargetGroup.userGroupName}.`);
    }
    catch (error) {
      console.error("Error leaving group:", error);
      setLeaveNotificationText("Ett fel uppstod när du skulle lämna gruppen. Kontakta administratörerna för att lösa problemet.");
    }
    finally {
      setLeaveNotificationOpen(true);
      setIsConfirmationOpen(false);
      setConfirmationAction(null);
      setLeaveTargetGroup(null);
    }
  }

  const confirmLeaveGroup = async () => {
    if (!leaveTargetGroup) return;

    const tenderingName = getModuleNameById(leaveTargetGroup.moduleID);
    const task: CreateTask = {
      taskName: `Begäran om att lämna gruppen ${leaveTargetGroup.userGroupName} i ramavtalet ${tenderingName} för användare ${user.user.email}`,
      taskDescription: `Användare ${user.user.email} har begärt att lämna gruppen ${leaveTargetGroup.userGroupName} i ramavtalet ${tenderingName}.\n\nVänligen granska och godkänn eller avslå denna begäran.`,
      taskData: {
        taskModuleID: leaveTargetGroup.moduleID,
        taskOrganizationID: user.organization.organizationID,
        userEmail: user.user.email,
        taskUserGroupID: leaveTargetGroup.userGroupID,
        requestType: 'Leave'
      }
    }

    try {
      const result = await createTenderingRequestTask(task);
      if (result.taskID !== 0) {
        setOwnPendingLeaveGroupIDs(prev => new Set(prev).add(leaveTargetGroup.userGroupID));
        setLeaveNotificationText("En begäran om att lämna gruppen har skickats till behörighetsansvariga.");
      }
      else {
        setLeaveNotificationText("En begäran om att lämna gruppen har redan skickats tidigare och väntar på godkännande.");
      }
    }
    catch (error) {
      console.error("Error creating leave request task:", error);
      setLeaveNotificationText("Ett fel uppstod när begäran skulle skickas. Kontakta administratörerna för att lösa problemet.");
    }
    finally {
      setLeaveNotificationOpen(true);
      setIsConfirmationOpen(false);
      setConfirmationAction(null);
      setLeaveTargetGroup(null);
    }
  }

  // Ramavtal the logged in user already belongs to (via at least one user group), the only ones they may request more groups in.
  // Derived from the freshly-fetched userGroups list rather than user.userGroupAssignments, which can be stale (cached at login).
  const myMemberUserGroups = userGroups.filter((g: any) => g.userGroupAssignments?.some((m: any) => m.user?.userID === user?.user.userID));
  const myUserGroupIDs = new Set<number>(myMemberUserGroups.map((g: any) => g.userGroupID));
  const myTenderingModuleIDs = new Set<number>(myMemberUserGroups.map((g: any) => g.moduleID));
  const myTenderings = tenderings.filter(t => myTenderingModuleIDs.has(t.moduleID));

  const resetRequestMoreGroups = () => {
    setMoreGroupsTenderingID("");
    setMoreGroupsAvailableUserGroups([]);
    setMoreGroupsSelectedUserGroupIDs([]);
    setMoreGroupsAdminsMap({});
    setLoadingMoreGroupsAdminIDs([]);
  };

  const handleOpenRequestMoreGroups = () => {
    resetRequestMoreGroups();
    setIsRequestMoreGroupsDialogOpen(true);
  };

  const handleCloseRequestMoreGroups = () => {
    setIsRequestMoreGroupsDialogOpen(false);
    setIsMoreGroupsGroupDialogOpen(false);
    resetRequestMoreGroups();
  };

  const handleContinueToMoreGroups = async () => {
    if (!moreGroupsTenderingID) return;

    try {
      const groups = await getUserGroupsByModuleAndOrganizationID(+moreGroupsTenderingID, user.organization.organizationID);
      const available = (groups ?? []).filter((g: any) => !myUserGroupIDs.has(g.userGroupID) && !ownPendingJoinGroupIDs.has(g.userGroupID));
      setMoreGroupsAvailableUserGroups(available);
    }
    catch (error) {
      console.error('Error fetching user groups for request:', error);
      setMoreGroupsAvailableUserGroups([]);
    }

    setIsRequestMoreGroupsDialogOpen(false);
    setIsMoreGroupsGroupDialogOpen(true);
  };

  const handleSelectMoreGroupUserGroups = async (userGroupIDs: string[]) => {
    setMoreGroupsSelectedUserGroupIDs(userGroupIDs);

    const newIDs = userGroupIDs.filter((id) => !(id in moreGroupsAdminsMap) && !loadingMoreGroupsAdminIDs.includes(id));
    if (newIDs.length === 0) return;

    setLoadingMoreGroupsAdminIDs(prev => [...prev, ...newIDs]);

    await Promise.all(newIDs.map(async (userGroupID) => {
      try {
        const admins = await getUsersByModuleAndRole(+moreGroupsTenderingID, "Behörighetsansvarig", +userGroupID);
        setMoreGroupsAdminsMap(prev => ({ ...prev, [userGroupID]: admins ?? [] }));
      }
      catch (error) {
        console.error('Error fetching behörighetsansvariga:', error);
        setMoreGroupsAdminsMap(prev => ({ ...prev, [userGroupID]: [] }));
      }
      finally {
        setLoadingMoreGroupsAdminIDs(prev => prev.filter((id) => id !== userGroupID));
      }
    }));
  };

  const confirmRequestMoreGroups = async () => {
    if (moreGroupsSelectedUserGroupIDs.length === 0) return;

    const tenderingName = getModuleNameById(+moreGroupsTenderingID);
    const selectedGroups = moreGroupsAvailableUserGroups.filter(g => moreGroupsSelectedUserGroupIDs.includes(g.userGroupID.toString()));

    const tasks: CreateTask[] = selectedGroups.map(group => ({
      taskName: `Begäran om att gå med i gruppen ${group.userGroupName} i ramavtalet ${tenderingName} för användare ${user.user.email}`,
      taskDescription: `Användare ${user.user.email} har begärt att gå med i gruppen ${group.userGroupName} i ramavtalet ${tenderingName}.\n\nVänligen granska och godkänn eller avslå denna begäran.`,
      taskData: {
        taskModuleID: +moreGroupsTenderingID,
        taskOrganizationID: user.organization.organizationID,
        userEmail: user.user.email,
        taskUserGroupID: group.userGroupID,
        requestType: 'Join'
      }
    }));

    try {
      const results = await Promise.all(tasks.map(task => createTenderingRequestTask(task)));
      const sentCount = results.filter(r => r.taskID !== 0).length;

      setOwnPendingJoinGroupIDs(prev => {
        const next = new Set(prev);
        selectedGroups.forEach(g => next.add(g.userGroupID));
        return next;
      });

      setLeaveNotificationText(
        sentCount === tasks.length
          ? "En begäran om att gå med i gruppen/grupperna har skickats till behörighetsansvariga."
          : "En eller flera av begäranden har redan skickats tidigare och väntar på godkännande."
      );
    }
    catch (error) {
      console.error('Error creating group join request:', error);
      setLeaveNotificationText("Ett fel uppstod när begäran skulle skickas. Kontakta administratörerna för att lösa problemet.");
    }
    finally {
      setLeaveNotificationOpen(true);
      handleCloseRequestMoreGroups();
    }
  };

  return (
    <>
    <Drawer
      size="large"
      position="end"
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          <div className="flex items-start justify-between w-full">
            <h2>
              Grupper
            </h2>
            <Button size="small" icon={<DismissRegular />} shape="circular" onClick={props.onClose}>
            </Button>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <TabList
          selectedValue={actualTab}
          onTabSelect={(_, data) => setActualTab(data.value as string)}
          className="flex-none"
        >
          <Tab value="groups">Grupper</Tab>
          {user && user.featurePermissions.some((fp: any) => fp.action == "See user groups requests") &&
            <Tab value="requests">Gruppbegäran</Tab>
          }
        </TabList>
        {actualTab === "groups" && <div className="flex flex-col gap-4">
          {/* Row 1: actions, one line each */}
          <div className="flex gap-2 mt-5 items-center">
            {user && user.featurePermissions.some((fp: any) => fp.action == "Create user group") &&
            <>
              <Button appearance="primary" style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setIsCreateUserDialogOpen(true)} icon={<PersonAddRegular />}>
                Skapa grupp
              </Button>
            </>
            }
            {user && user.featurePermissions.some((fp: any) => fp.action == "Copy user group") &&
              <Button style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setIsCopyGroupsDialogOpen(true)} icon={<CopyRegular />}>
                Kopiera grupper
              </Button>
            }
            <Button appearance="primary" className="ml-auto" style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={handleOpenRequestMoreGroups} icon={<AddRegular />}>
              Ansök om fler grupper
            </Button>
          </div>
          {/* Row 2: search and filters */}
          <div className="flex gap-2 flex-wrap">
            <Input
              contentBefore={<SearchRegular />}
              placeholder="Sök grupp, ramavtal eller medlem"
              value={groupSearch}
              onChange={(_e, data) => setGroupSearch(data.value)}
              style={{ flex: '1 1 220px', minWidth: 0 }}
            />
            {user && user.roles.some((r: RoleDTO) => r.roleType == 'General') &&
            <>
              <Dropdown
                style={{ flex: '1 1 160px', minWidth: 0 }}
                placeholder="Organisation"
                value={selectedOrganization ? organizations.find(o => o.organizationID.toString() === selectedOrganization)?.organizationName : ""}
                onOptionSelect={(e, data) => handleOrganizationSelect(data.optionValue as string)}
                clearable
                positioning={{autoSize: false}}
                listbox={{
                  style: {
                    maxHeight: '300px',
                    overflowY: 'auto'
                  },
                }}
              >
                {organizations.map((organization) => (
                  <Option key={organization.organizationID} value={organization.organizationID.toString()}>
                    {organization.organizationName}
                  </Option>
                ))}
              </Dropdown>
              <Dropdown
                style={{ flex: '1 1 160px', minWidth: 0 }}
                placeholder="Ramavtal"
                value={selectedRamavtal ? tenderings.find(t => t.moduleID.toString() === selectedRamavtal)?.moduleName : ""}
                onOptionSelect={(e, data) => handleRamavtalSelect(data.optionValue as string)}
                clearable
                positioning={{autoSize: false}}
                listbox={{
                  style: {
                    maxHeight: '300px',
                    overflowY: 'auto'
                  },
                }}
              >
                {tenderings.map((tendering) => (
                  <Option key={tendering.moduleID} value={tendering.moduleID.toString()}>
                    {tendering.moduleName}
                  </Option>
                ))}
              </Dropdown>
            </>}
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner size="large" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className="w-1/5">
                      <Subtitle2>Ramavtal</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/5">
                      <Subtitle2>Gruppnamn</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/5">
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUserGroups.length > 0 ? (
                    filteredUserGroups.map((userGroup, index) => (
                      <TableRow key={userGroup.userGroupID || index} className="group">
                        <TableCell className="w-1/5">
                          <Text>{getModuleNameById(userGroup.moduleID)}</Text>
                        </TableCell>
                        <TableCell className="w-1/5 break-all">
                          <Text>{userGroup.userGroupName}</Text>
                        </TableCell>
                        <TableCell className="w-1/5">
                        <div className="flex gap-1 items-center flex-wrap">
                          {(isUserGroupAdmin(userGroup) || (user && user.roles.some((r: RoleDTO) => r.roleType == 'General'))) ? (
                            <Tooltip content={`Hantera ${userGroup.userGroupName}`} relationship="label">
                              <Button appearance="subtle" shape="circular" icon={<SettingsRegular />} onClick={() => handleAddUsersClick(userGroup)} />
                            </Tooltip>
                          ) : isGroupMember(userGroup) && (
                            <Tooltip content="Se grupp" relationship="label">
                              <Button appearance="subtle" shape="circular" icon={<EyeRegular />} onClick={() => handleAddUsersClick(userGroup)} />
                            </Tooltip>
                          )}
                          {isGroupMember(userGroup) && (
                            ownPendingLeaveGroupIDs.has(userGroup.userGroupID) ? (
                              <Badge color="warning" appearance="tint">Begäran om att lämna skickad</Badge>
                            ) : (
                              <Tooltip content={isUserGroupAdmin(userGroup) ? "Lämna gruppen" : "Begär att lämna gruppen"} relationship="label">
                                <Button appearance="subtle" shape="circular" icon={<PersonSubtractRegular />} onClick={() => handleLeaveGroupClick(userGroup)} />
                              </Tooltip>
                            )
                          )}
                          {canDeleteUserGroup(userGroup) &&
                            <Tooltip content={isUserGroupEmpty(userGroup) ? "Radera grupp" : "Gruppen måste vara tom för att kunna raderas"} relationship="label">
                              <Button
                                className="h-auto"
                                icon={<DeleteRegular />}
                                disabled={!isUserGroupEmpty(userGroup)}
                                onClick={() => onDeleteGroupClick(userGroup)}
                              ></Button>
                            </Tooltip>
                          }
                        </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className="flex justify-center py-8">
                          <Text>Inga grupper hittades</Text>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {/* <div className="flex justify-between mt-4">
                <div>Antal objekt per sida: 50</div>
                <div className='flex gap-2 items-center'>
                  <Button disabled={page == 0} onClick={() => setPage(page - 1)} icon={<ArrowLeftRegular />}></Button>
                  <Button disabled={userCount < 50 + 50*page} onClick={() => setPage(page + 1)} icon={<ArrowRightRegular />}></Button>
                  {1+(50*page)} - {(userCount > (50+(50*page))) ? 50+(50*page) : userCount} av {userCount}
                </div>
              </div> */}
            </div>
          )}
        </div>}
        {actualTab === "requests" && <div className="flex flex-col gap-4 mt-5">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner size="large" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className="w-[25%]">
                      <Subtitle2>Användare</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-[20%]">
                      <Subtitle2>Gruppnamn</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-[20%]">
                      <Subtitle2>Ramavtal</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-[15%]">
                      <Subtitle2>Typ</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-[15%]">
                      <Subtitle2>Skapad</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-[20%]">
                      <Subtitle2>Åtgärder</Subtitle2>
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userTasks.length > 0 ? (
                    userTasks.map((task: TaskListItem, index) => (
                      <TableRow key={task.taskID || index} className="group">
                        <TableCell className="w-[25%] max-w-0">
                          <Text block className="break-all">{getTaskData(task.taskData).UserEmail}</Text>
                        </TableCell>
                        <TableCell className="w-[20%] break-all">
                          <Text>{getUserGroupName(getTaskData(task.taskData).TaskUserGroupID)}</Text>
                        </TableCell>
                        <TableCell className="w-[20%]">
                          <Text>
                            {getModuleNameById(task.taskModuleID)}
                          </Text>
                        </TableCell>
                        <TableCell className="w-[15%]">
                          {getRequestType(task) === 'Leave'
                            ? <Badge color="danger" appearance="tint">Vill lämna</Badge>
                            : <Badge color="success" appearance="tint">Vill gå med</Badge>}
                        </TableCell>
                        <TableCell className="w-[15%]">
                          <Text>{convertToLocaleDateString(task.taskCreated as unknown as string)}</Text>
                        </TableCell>
                        <TableCell className="w-[20%]">
                          <div className="flex gap-2">
                          <Tooltip content="Godkänn begäran" relationship="label">
                            <Button onClick={() => onTaskAccepted(task.taskID)} shape="circular" appearance="primary" icon={<CheckmarkRegular />}></Button>
                          </Tooltip>
                          <Tooltip content="Neka begäran" relationship="label">
                            <Button onClick={() => onTaskDenied(task.taskID)} shape="circular" icon={<DismissRegular />}></Button>
                          </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className="flex justify-center py-8">
                          <Text>Inga uppgifter hittades</Text>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>}
      </DrawerBody>
    </Drawer>
    <UserGroupsDialog
      open={isCreateUserDialogOpen}
      onClose={() => setIsCreateUserDialogOpen(false)}
      onGroupCreated={handleUsersAdded}
    />
    <CopyUserGroupsDialog
      open={isCopyGroupsDialogOpen}
      onClose={() => setIsCopyGroupsDialogOpen(false)}
      onCopied={handleUsersAdded}
    />
    <UserGroupMembersDrawer
      open={isGroupMembersDrawerOpen}
      onClose={() => {
        setIsGroupMembersDrawerOpen(false);
        setSelectedGroup(null);
      }}
      userGroup={selectedGroup}
      onGroupUpdated={handleUsersAdded}
    />
    <ConfirmationDialog
      isOpen={isConfirmationOpen}
      onClose={() => {
        setIsConfirmationOpen(false);
        setSelectedTaskID(null);
        setSelectedGroupToDelete(null);
        setConfirmationAction(null);
        setDenyComment("");
        setLeaveTargetGroup(null);
      }}
      onConfirm={
        confirmationAction === 'accept' ? handleAcceptConfirmed
        : confirmationAction === 'delete' ? handleDeleteConfirmed
        : confirmationAction === 'leaveDirect' ? confirmLeaveGroupDirect
        : confirmationAction === 'leaveRequest' ? confirmLeaveGroup
        : handleDenyConfirmed
      }
      title={
        confirmationAction === 'accept' ? 'Godkänn begäran'
        : confirmationAction === 'delete' ? 'Radera grupp'
        : confirmationAction === 'leaveDirect' ? 'Lämna grupp'
        : confirmationAction === 'leaveRequest' ? 'Begär att lämna grupp'
        : 'Neka begäran'
      }
      text={(() => {
        const task = userTasks.find(t => t.taskID === selectedTaskID);
        const isLeave = task ? getRequestType(task) === 'Leave' : false;
        if (confirmationAction === 'leaveDirect') {
          return `Är du säker på att du vill lämna gruppen "${leaveTargetGroup?.userGroupName}"?`;
        }
        if (confirmationAction === 'leaveRequest') {
          return `Vill du skicka en begäran om att lämna gruppen "${leaveTargetGroup?.userGroupName}"? Begäran skickas till gruppens behörighetsansvariga för godkännande.`;
        }
        if (confirmationAction === 'accept') {
          return isLeave
            ? 'Är du säker på att du vill godkänna denna begäran om att lämna gruppen?'
            : 'Är du säker på att du vill godkänna denna begäran om att gå med i gruppen?';
        }
        return isLeave
          ? 'Är du säker på att du vill neka denna begäran om att lämna gruppen?'
          : confirmationAction === 'delete'
        ? `Är du säker på att du vill radera gruppen "${selectedGroupToDelete?.userGroupName}"? Detta går inte att ångra.`
        : 'Är du säker på att du vill neka denna begäran om att gå med i gruppen?';
      })()}
      confirmText={
        confirmationAction === 'deny' ? 'Neka begäran'
        : confirmationAction === 'leaveRequest' ? 'Skicka begäran'
        : undefined
      }
    >
    </ConfirmationDialog>
    <NotificationDialog
      isOpen={leaveNotificationOpen}
      message={leaveNotificationText}
      onClose={() => setLeaveNotificationOpen(false)}
    />
    <ConfirmationDialog
      isOpen={isRequestMoreGroupsDialogOpen}
      onClose={handleCloseRequestMoreGroups}
      onConfirm={handleContinueToMoreGroups}
      title="Ansök om fler grupper"
      text={
        myTenderings.length === 0
          ? "Du tillhör inte någon grupp i något ramavtal än."
          : "Välj vilket ramavtal du vill ansöka om fler grupper i:"
      }
      confirmText="Nästa"
      fixedHeight={myTenderings.length > 0}
    >
      {myTenderings.length > 0 && (
        <Field label="Ramavtal" className="mt-2">
          <Dropdown
            placeholder="Välj ett ramavtal"
            value={myTenderings.find(t => t.moduleID.toString() === moreGroupsTenderingID)?.moduleName ?? ""}
            onOptionSelect={(_e, data) => setMoreGroupsTenderingID(data.optionValue as string)}
            // DialogContent scrolls internally, which otherwise clips the popup's available height to the dialog's bounds
            positioning={{ autoSize: false, overflowBoundary: 'window' }}
            listbox={{
              style: {
                maxHeight: '250px',
                overflowY: 'auto'
              },
            }}
          >
            {myTenderings.map((tendering) => (
              <Option key={tendering.moduleID} value={tendering.moduleID.toString()}>
                {tendering.moduleName}
              </Option>
            ))}
          </Dropdown>
        </Field>
      )}
    </ConfirmationDialog>
    <TenderingRequestDialog
      isOpen={isMoreGroupsGroupDialogOpen}
      tenderingName={getModuleNameById(+moreGroupsTenderingID)}
      userGroups={moreGroupsAvailableUserGroups}
      selectedUserGroupIDs={moreGroupsSelectedUserGroupIDs}
      onSelectUserGroups={handleSelectMoreGroupUserGroups}
      groupAdminsByUserGroupID={moreGroupsAdminsMap}
      loadingGroupAdminIDs={loadingMoreGroupsAdminIDs}
      onConfirm={confirmRequestMoreGroups}
      onClose={handleCloseRequestMoreGroups}
    />
  </>
  );
};

export default UserGroupsDrawer;